import { useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useField, useFormikContext } from 'formik'

import { EMPTY_LIST } from '../../../constants/common'
import { FILE_SCOPE_REQUEST } from '../../../constants/file'
import { isValueFunctionType } from '../../../utils/common'
import {
  FilesPreviewGallery,
  useFilePreviewGallery,
} from '../../FilesPreviewGallery'
import { useFileUpload } from '../File.hooks'
import { FileList } from '../FileList'
import { FilePreview } from '../FilePreview'
import { FileUpload } from '../FileUpload'
import { FileUploadPlaceholder } from '../FileUploadPlaceholder'

function FileFormField({
  disabled,
  error,
  fileListItemClassName,
  filesOptions: { getFiles, setFileError, setFileUploading, onDeleteFile },
  name,
  uploadPlaceholderClassName,
}) {
  const { t } = useTranslation()

  const { setFieldValue } = useFormikContext()

  const [field] = useField(name)

  // Single source of truth: the Formik field value (restored from draft).
  const files = field.value || EMPTY_LIST

  function setFiles(updater) {
    const currentFiles = field.value || EMPTY_LIST
    const nextFiles = isValueFunctionType(updater)
      ? updater(currentFiles)
      : updater

    setFieldValue(name, nextFiles)
  }

  const {
    filesError,
    filesUploading,
    handleRemoveFile,
    handleStartUploading,
    handleUploadErrors,
    handleUploadFile,
  } = useFileUpload({
    files,
    onDeleteFile,
    onSetFiles: setFiles,
  })

  useEffect(() => {
    setFileUploading?.({ enabled: filesUploading.length > 0, fieldName: name })
    setFileError?.({ enabled: filesError.length > 0, fieldName: name })

    return () => {
      setFileUploading?.({ enabled: false, fieldName: name })
      setFileError?.({ enabled: false, fieldName: name })
    }
  }, [
    filesError.length,
    filesUploading.length,
    name,
    setFileError,
    setFileUploading,
  ])

  const { activeFileIndex, handleClickFile, handleCloseFilePreview } =
    useFilePreviewGallery({ files })

  function handleStartUpload(nextFiles) {
    handleStartUploading(nextFiles)
  }

  function handleFinishUpload(acceptedFiles) {
    handleUploadFile(acceptedFiles)
  }

  function handleErrorUpload(errors) {
    handleUploadErrors(errors)
  }

  function renderFile(file, key) {
    return (
      <FilePreview
        disabled={disabled}
        file={file}
        key={key}
        onClick={handleClickFile}
        onRemoveFile={handleRemoveFile}
      />
    )
  }

  return (
    <>
      <FileList
        errorItems={filesError}
        files={files}
        itemClassName={fileListItemClassName}
        renderItem={renderFile}
        uploadingItems={filesUploading}
        onRemoveFile={handleRemoveFile}
      >
        <FileUpload
          disabled={disabled}
          getFiles={getFiles}
          onFinishUpload={handleFinishUpload}
          onStartUpload={handleStartUpload}
          onUploadErrors={handleErrorUpload}
        >
          <FileUploadPlaceholder
            className={uploadPlaceholderClassName}
            disabled={disabled}
            error={error}
            text={t('File.ClickToUploadKey')}
          />
        </FileUpload>
      </FileList>

      <FilesPreviewGallery
        files={files}
        initActive={activeFileIndex}
        scope={FILE_SCOPE_REQUEST}
        onClose={handleCloseFilePreview}
      />
    </>
  )
}

export default FileFormField
